<script lang="ts">
	import { onMount } from 'svelte';
	import { editionsGallerySort } from '../../constants/sorting';
	import {
		GetEditionsGalleryDocument,
		type GetEditionsGalleryQueryVariables,
	} from '../../queries/__generated__/getEditionsGallery.generated';
	import type { EditionsGalleryCardEdition } from '../../utils/getEditionsGalleryCard/getEditionsGalleryCard';
	import { EditionsGallery } from '../EditionsGallery';
	import { infiniteApiQuery } from '#lib/api/infiniteApiQuery/infiniteApiQuery.js';
	import { FeatureFlags } from '#lib/constants/feature-flags.js';
	import { gqlClient } from '#lib/gqlClient.js';
	import {
		editionsGalleryItemsQueryKey,
		getEditionsGalleryItems,
		type EditionsGalleryItemsParams,
	} from '#lib/websites/editions/api/getEditionsGalleryPage.js';
	import { EDITIONS_GALLERY_LIMIT } from '#lib/websites/editions/constants/gallery.js';
	import { page } from '$app/state';
	import type { FilterSections } from '$global/features/filter-drawer/types';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import { getInfiniteData } from '$global/utils/infinite-loading/getInfiniteData';
	import { infiniteQuery } from '$global/utils/infinite-loading/infiniteQuery';

	interface EditionsGalleryBrowseData {
		filterSections: FilterSections;
		variables: GetEditionsGalleryQueryVariables;
		params: EditionsGalleryItemsParams;
	}

	interface Props {
		dataCy: string;
	}

	let { dataCy }: Props = $props();

	// While the flag is off the page data and the load-more query are the
	// GraphQL ones; each branch reads the page data through its own shape
	// (rest-migration/feature-flags.md).
	const useRest = FeatureFlags.RestPages['/editions/gallery'];

	let data = $derived(page.data as unknown as EditionsGalleryBrowseData);

	let isReady = $state(false);

	onMount(() => {
		isReady = true;
	});

	let filterSections = $derived(data.filterSections);

	let restQuery = $derived(
		useRest
			? infiniteApiQuery({
					queryKey: editionsGalleryItemsQueryKey(data.params),
					queryFn: (offset) =>
						getEditionsGalleryItems(undefined, {
							...data.params,
							offset,
						}),
					limit: EDITIONS_GALLERY_LIMIT,
				})
			: undefined
	);

	let gqlQuery = $derived(
		useRest
			? undefined
			: infiniteQuery({
					gqlClient,
					variables: data.variables,
					document: GetEditionsGalleryDocument,
					limit: EDITIONS_GALLERY_LIMIT,
				})
	);

	interface GridRow {
		artists?:
			({ Artists_id?: { name?: string | null } | null } | null)[] | null;
		image?: {
			filename_disk?: string | null;
			width?: number | null;
			height?: number | null;
		} | null;
	}

	const toCardEdition = <T extends GridRow>(item: T) => ({
		...item,
		image: {
			url: getImageUrl(item?.image?.filename_disk, '512px'),
			width: item?.image?.width || 0,
			height: item?.image?.height || 0,
		},
		artists: (item?.artists || [])
			.map((artist) => artist?.Artists_id?.name || '')
			.join(', '),
	});

	let editions = $derived(
		useRest
			? getInfiniteData({
					query: restQuery!,
					// The REST row differs from the GraphQL one only in its integer
					// ids, which the card never reads.
					transform: (page) =>
						page.editions.map(
							(item) =>
								toCardEdition(item) as unknown as EditionsGalleryCardEdition
						),
				})
			: getInfiniteData({
					query: gqlQuery!,
					transform: (page) =>
						page.Editions.map(
							(item) => toCardEdition(item) as EditionsGalleryCardEdition
						),
				})
	);

	let count = $derived(
		useRest
			? restQuery!.data?.pages[0]?.total || 0
			: gqlQuery!.data?.pages[0]?.Editions_aggregated[0]?.count?.name || 0
	);
	let hasMore = $derived(count > (editions?.length || 0));

	const handleLoadMore = () => {
		if (useRest) {
			restQuery!.fetchNextPage();
			return;
		}

		gqlQuery!.fetchNextPage();
	};

	let query = $derived(useRest ? restQuery! : gqlQuery!);
	let loading = $derived(
		(query.isFetching && !query.isFetchingNextPage) || !isReady
	);
	let loadingNextPage = $derived(query.isFetchingNextPage);

	const sortOptions = [editionsGallerySort];
</script>

<EditionsGallery
	{dataCy}
	{editions}
	{filterSections}
	{sortOptions}
	{loading}
	{loadingNextPage}
	onLoadMore={handleLoadMore}
	{count}
	showSeriesOnlyWhenPreview
	showSubSeriesAsCards
	{hasMore}
	initialLoadMoreButton={FeatureFlags.loadButtonBeforeInfiniteScroll}
	trackPrefix="EDITIONS"
	listId="editions_gallery"
/>
